/**
 * Session memory for list-view URL state (filters + sort).
 *
 * The Action Items list carries every filter and the sort in the URL so
 * deep-links and refresh work. Navigating away to an item detail and back
 * ("← Back to Action Items") lands on the bare route, which dropped the
 * whole selection. These helpers let a list remember its own view for the
 * browser session, keyed by the signed-in account so a second person on
 * the same machine never inherits the first one's view.
 *
 * Storage is `sessionStorage` (per tab, cleared when the tab closes) —
 * the user's ask was session-scoped, not permanent.
 *
 * The functions here are pure over `URLSearchParams` so they can be tested
 * without a DOM; the component owns the effects that call them.
 */

/** Serialize just the remembered keys, in a fixed order so two equal views
 *  always produce the identical string (the component compares them). */
export function serializeViewState(params: URLSearchParams, keys: readonly string[]): string {
  const out = new URLSearchParams();
  for (const k of keys) {
    const v = params.get(k);
    if (v !== null && v !== "") out.set(k, v);
  }
  return out.toString();
}

/** True when the URL already carries a remembered key — a deep link or an
 *  in-app redirect that pre-set the view. Those always win over memory. */
export function viewStateInUrl(params: URLSearchParams, keys: readonly string[]): boolean {
  return keys.some((k) => {
    const v = params.get(k);
    return v !== null && v !== "";
  });
}

/** Merge a remembered view onto the current params: remembered keys are
 *  overwritten (or cleared), anything else in the URL is left alone. */
export function applyViewState(
  current: URLSearchParams,
  stored: string,
  keys: readonly string[]
): URLSearchParams {
  const restored = new URLSearchParams(stored);
  const next = new URLSearchParams(current);
  for (const k of keys) {
    const v = restored.get(k);
    if (v === null || v === "") next.delete(k);
    else next.set(k, v);
  }
  return next;
}

export function readViewState(storageKey: string): string | null {
  try {
    return sessionStorage.getItem(storageKey);
  } catch {
    return null; // private mode / storage disabled — memory is best-effort
  }
}

export function writeViewState(storageKey: string, serialized: string): void {
  try {
    sessionStorage.setItem(storageKey, serialized);
  } catch {
    /* ignore */
  }
}
